import { useState, useEffect } from 'react';
import { D, st, toArr, apiGet, apiPost, apiPut, apiDel, capitalizeWords } from '../constants';
import { MAT_CATALOG_FLAT } from '../data.js';
import { Field, Overlay, MHdr } from './shared';

export default function Inventory({ me, jobs, matPrices }) {
  const isOwner = me?.user?.role === 'owner';
  const isApprentice = me?.user?.role === 'apprentice' || me?.user?.role === 'helper';
  const canEdit = !isApprentice;
  const myTruckId = me?.user?.truck_id;

  const [trucks, setTrucks] = useState([]);
  const [truckId, setTruckId] = useState('');
  const [subTab, setSubTab] = useState('stock');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);

  const [form, setForm] = useState({ desc:'', unit:'ea', qty:'', unitCost:'' });
  const [busy, setBusy] = useState(false);

  const [newTruckName, setNewTruckName] = useState('');
  const [truckBusy, setTruckBusy] = useState(false);

  const [useModal, setUseModal] = useState(null);
  const [useQty, setUseQty] = useState('');
  const [useJobId, setUseJobId] = useState('');
  const [useBusy, setUseBusy] = useState(false);

  const [team, setTeam] = useState(null);
  const [teamMsg, setTeamMsg] = useState(null);
  const [teamBusy, setTeamBusy] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [newMember, setNewMember] = useState({ name:'', email:'', phone:'', role:'apprentice', payType:'Hourly', rate:'', pin:'', truckId:'' });

  const loadTrucks = () => {
    apiGet('/data/trucks').then(ts => {
      setTrucks(ts);
      if (isOwner && ts.length && !truckId) setTruckId(String(ts[0].id));
    }).catch(()=>{});
  };

  const loadItems = () => {
    setLoading(true);
    const qs = (isOwner && truckId) ? ('?truckId='+truckId) : '';
    apiGet('/data/inventory'+qs).then(setItems).catch(()=>setItems([])).finally(()=>setLoading(false));
  };

  const loadTeam = () => { apiGet('/data/users').then(setTeam).catch(()=>{}); };

  useEffect(() => { loadTrucks(); if (isOwner) loadTeam(); }, []);
  useEffect(() => { loadItems(); }, [truckId]);

  const selectTruck = (id) => { setTruckId(id); setSubTab('stock'); };

  const addItem = async () => {
    if (!form.desc.trim()) return;
    const tId = isOwner ? truckId : myTruckId;
    if (!tId) { setMsg({ok:false,text:'No truck selected'}); return; }
    setBusy(true); setMsg(null);
    try {
      await apiPost('/data/inventory', { truckId: Number(tId), desc: form.desc.trim(), unit: form.unit, qty: Number(form.qty)||0, unitCost: Number(form.unitCost)||0 });
      setForm({ desc:'', unit:'ea', qty:'', unitCost:'' });
      setMsg({ok:true,text:'Added.'});
      loadItems();
    } catch(e) { setMsg({ok:false,text:'Error: '+e.message}); }
    setBusy(false);
  };

  const deleteItem = async (id) => {
    if (!confirm('Remove this item from inventory?')) return;
    await apiDel('/data/inventory/'+id);
    loadItems();
  };

  const createTruck = async () => {
    if (!newTruckName.trim()) return;
    setTruckBusy(true);
    try {
      const trimmed = newTruckName.trim();
      const name = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
      const r = await apiPost('/data/trucks', { name });
      setNewTruckName('');
      loadTrucks();
      selectTruck(String(r.id));
    } catch(e) { alert('Error: '+e.message); }
    setTruckBusy(false);
  };

  const deleteTruck = async (t) => {
    if (!confirm(`Delete truck "${t.name}"? This also removes all stock on it and unassigns any crew.`)) return;
    try {
      await apiDel('/data/trucks/'+t.id);
      if (String(t.id)===truckId) setTruckId('');
      loadTrucks();
      if (isOwner) loadTeam();
    } catch(e) { alert('Error: '+e.message); }
  };

  const openUse = (item) => { setUseModal(item); setUseQty(''); setUseJobId(''); };

  const confirmUse = async () => {
    if (!useModal || !useQty || !useJobId) return;
    setUseBusy(true);
    try {
      await apiPost(`/data/inventory/${useModal.id}/use`, { qty: Number(useQty), jobId: Number(useJobId) });
      setUseModal(null);
      loadItems();
    } catch(e) { alert('Error: '+e.message); }
    setUseBusy(false);
  };

  const openAddMember = () => { setNewMember(m=>({...m, truckId})); setAddingMember(true); };

  const addMember = async () => {
    if (!newMember.name.trim()) return;
    setTeamBusy(true); setTeamMsg(null);
    try {
      await apiPost('/data/users', { ...newMember, name: capitalizeWords(newMember.name), rate: Number(newMember.rate)||0 });
      setNewMember({ name:'', email:'', phone:'', role:'apprentice', payType:'Hourly', rate:'', pin:'', truckId:'' });
      setAddingMember(false);
      loadTeam();
      setTeamMsg({ ok:true, text:'Team member added.' });
    } catch (e) {
      let m = e.message; try { m = JSON.parse(m).error || m; } catch {}
      setTeamMsg({ ok:false, text: m });
    }
    setTeamBusy(false);
  };

  const updateMemberTruck = async (u, tId) => {
    try { await apiPut('/data/users/'+u.id, { truckId: tId||null }); loadTeam(); } catch(e) { setTeamMsg({ok:false,text:'Error: '+e.message}); }
  };

  const activeJobs = toArr(jobs).filter(j => j.status!=='Completed');
  const currentTruck = trucks.find(t=>String(t.id)===truckId);
  const crewOnThisTruck = team ? team.filter(u=>u.role!=='owner' && String(u.truck_id)===String(truckId)) : [];
  const unassignedCrew = team ? team.filter(u=>u.role!=='owner' && !u.truck_id) : [];

  return (
    <div>
      <div style={st.h1}>Truck</div>
      <div style={{color:D.muted,marginBottom:16,fontSize:13,maxWidth:560}}>
        {isOwner ? 'Manage trucks, crew assignments, and stock.' :
         isApprentice ? 'View-only \u2014 ask your foreman to add or use stock.' :
         'Manage materials on your truck.'}
      </div>

      {isOwner && (
        <div style={{...st.card, marginBottom:16}}>
          <div style={{fontWeight:600,marginBottom:12}}>Trucks</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
            {trucks.map(t => (
              <div key={t.id} style={{display:'flex',alignItems:'center',gap:2,background:String(t.id)===truckId?D.accent+'22':D.bg,border:`1px solid ${String(t.id)===truckId?D.accent:D.border}`,borderRadius:8,padding:'2px 2px 2px 4px'}}>
                <button style={{background:'none',border:'none',color:D.text,cursor:'pointer',fontSize:13,padding:'6px 8px'}} onClick={()=>selectTruck(String(t.id))}>{t.name}</button>
                <button style={{...st.btn('danger'),fontSize:11,padding:'4px 7px'}} onClick={()=>deleteTruck(t)}>✕</button>
              </div>
            ))}
            {trucks.length===0 && <span style={{fontSize:13,color:D.muted}}>No trucks yet.</span>}
          </div>
          <div style={{display:'flex',gap:8}}>
            <input style={{...st.input,flex:1}} placeholder="New truck name" value={newTruckName} onChange={e=>setNewTruckName(e.target.value)}/>
            <button style={st.btn()} disabled={truckBusy||!newTruckName.trim()} onClick={createTruck}>+ Add Truck</button>
          </div>
        </div>
      )}

      {!isOwner && !myTruckId && (
        <div style={{...st.card,color:D.muted,fontSize:13}}>You haven't been assigned to a truck yet. Ask your owner to assign one.</div>
      )}

      {(isOwner ? truckId : myTruckId) && (
        <div>
          {isOwner && (
            <div style={{display:'flex',gap:8,marginBottom:16}}>
              <button style={subTab==='stock'?st.btn('primary'):st.btn()} onClick={()=>setSubTab('stock')}>Truck Stock</button>
              <button style={subTab==='crew'?st.btn('primary'):st.btn()} onClick={()=>setSubTab('crew')}>Add Crew</button>
            </div>
          )}

          {(!isOwner || subTab==='stock') && (
            <div style={st.card}>
              {canEdit && (
                <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr auto',gap:8,marginBottom:16,alignItems:'end'}}>
                  <Field label="Material"><input style={st.input} list="matSuggestions" value={form.desc} onChange={e=>{
                    const desc=e.target.value;
                    const existing=items.find(i=>i.desc.toLowerCase()===desc.toLowerCase());
                    const catalogMatch=MAT_CATALOG_FLAT.find(m=>m.desc.toLowerCase()===desc.toLowerCase());
                    const catalogCost=matPrices&&matPrices[desc]!==undefined?matPrices[desc]:catalogMatch?.cost;
                    setForm(f=>({...f,desc,
                      unit: existing?existing.unit:(catalogMatch?catalogMatch.unit:f.unit),
                      unitCost: existing?String(existing.unit_cost):(catalogCost!==undefined?String(catalogCost):f.unitCost),
                    }));
                  }} placeholder="e.g. 12 AWG wire"/>
                  <datalist id="matSuggestions">
                    {[...new Set([...items.map(i=>i.desc), ...MAT_CATALOG_FLAT.map(m=>m.desc)])].map(d=><option key={d} value={d}/>)}
                  </datalist></Field>
                  <Field label="Unit"><input style={st.input} value={form.unit} onChange={e=>setForm(f=>({...f,unit:e.target.value}))} placeholder="ft, ea, box"/></Field>
                  <Field label="Qty"><input style={st.input} type="number" value={form.qty} onChange={e=>setForm(f=>({...f,qty:e.target.value}))}/></Field>
                  <Field label="Unit Cost ($)"><input style={st.input} type="number" step="0.01" value={form.unitCost} onChange={e=>setForm(f=>({...f,unitCost:e.target.value}))}/></Field>
                  <button style={st.btn('primary')} disabled={busy||!form.desc.trim()} onClick={addItem}>+ Add</button>
                </div>
              )}
              {msg && <div style={{fontSize:13,color:msg.ok?D.success:D.danger,marginBottom:10}}>{msg.text}</div>}

              {loading ? <div style={{color:D.muted,fontSize:13}}>Loading...</div> : items.length===0 ? (
                <div style={{color:D.muted,fontSize:13,padding:'20px 0',textAlign:'center'}}>No stock on this truck yet.</div>
              ) : (
                <div style={{overflowX:'auto'}}><table style={st.table}>
                  <thead><tr>{["Material","Qty on Hand","Unit Cost","Value",""].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {items.map(it => (
                      <tr key={it.id}>
                        <td style={st.td}>{it.desc}</td>
                        <td style={st.td}>{it.qty_on_hand} {it.unit}</td>
                        <td style={st.td}>${Number(it.unit_cost).toFixed(2)}/{it.unit}</td>
                        <td style={st.td}>${(it.qty_on_hand*it.unit_cost).toFixed(2)}</td>
                        <td style={st.td}>
                          <div style={{display:'flex',gap:6}}>
                            {canEdit && <button style={{...st.btn(),fontSize:11,padding:'2px 8px'}} onClick={()=>openUse(it)}>Use on Job</button>}
                            {canEdit && <button style={{...st.btn('danger'),fontSize:11,padding:'2px 8px'}} onClick={()=>deleteItem(it.id)}>Remove</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              )}
            </div>
          )}

          {isOwner && subTab==='crew' && (
            <div style={st.card}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                <div style={{ fontWeight:600 }}>Crew on {currentTruck?currentTruck.name:'this truck'}</div>
                <button onClick={() => addingMember ? setAddingMember(false) : openAddMember()} style={st.btn()}>{addingMember ? 'Cancel' : '+ Add Member'}</button>
              </div>

              {addingMember && (
                <div style={{ marginBottom:16, padding:12, background:D.bg, borderRadius:8 }}>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
                    <input style={st.input} placeholder="Name" value={newMember.name} onChange={e=>setNewMember(m=>({...m,name:e.target.value}))}/>
                    <select style={st.input} value={newMember.role} onChange={e=>setNewMember(m=>({...m,role:e.target.value}))}>
                      <option value="apprentice">Apprentice</option>
                      <option value="foreman">Foreman</option>
                      <option value="owner">Owner</option>
                    </select>
                    <input style={st.input} placeholder="Email (optional)" value={newMember.email} onChange={e=>setNewMember(m=>({...m,email:e.target.value}))}/>
                    <input style={st.input} placeholder="Phone (optional)" value={newMember.phone} onChange={e=>setNewMember(m=>({...m,phone:e.target.value}))}/>
                    <input style={st.input} placeholder="Hourly rate" type="number" value={newMember.rate} onChange={e=>setNewMember(m=>({...m,rate:e.target.value}))}/>
                    <input style={st.input} placeholder="Initial PIN (4+ digits)" value={newMember.pin} onChange={e=>setNewMember(m=>({...m,pin:e.target.value}))}/>
                    <select style={st.input} value={newMember.truckId} onChange={e=>setNewMember(m=>({...m,truckId:e.target.value}))}>
                      <option value="">— No truck —</option>
                      {trucks.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <button onClick={addMember} disabled={teamBusy || !newMember.name.trim()} style={{ ...st.btn('primary'), opacity:(teamBusy||!newMember.name.trim())?0.6:1 }}>
                    {teamBusy ? 'Adding...' : 'Add Member'}
                  </button>
                </div>
              )}

              {!team ? (
                <div style={{ fontSize:13, color:D.muted }}>Loading...</div>
              ) : (
                <div>
                  {crewOnThisTruck.map(u => (
                    <div key={u.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${D.border}` }}>
                      <div>
                        <strong style={{ fontSize:13 }}>{u.name}</strong>
                        <span style={{ fontSize:11, color:D.muted, marginLeft:8 }}>{u.role}{u.email ? ` \u00b7 ${u.email}` : ''}</span>
                      </div>
                      <button style={{...st.btn(),fontSize:11,padding:'4px 10px'}} onClick={()=>updateMemberTruck(u,'')}>Remove from Truck</button>
                    </div>
                  ))}
                  {crewOnThisTruck.length===0 && <div style={{fontSize:13,color:D.muted,padding:'8px 0'}}>No crew on this truck yet.</div>}

                  {unassignedCrew.length>0 && (
                    <div style={{marginTop:16}}>
                      <div style={{fontSize:12,color:D.muted,marginBottom:8}}>Unassigned crew</div>
                      {unassignedCrew.map(u => (
                        <div key={u.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:`1px solid ${D.border}` }}>
                          <div>
                            <strong style={{ fontSize:13 }}>{u.name}</strong>
                            <span style={{ fontSize:11, color:D.muted, marginLeft:8 }}>{u.role}{u.email ? ` \u00b7 ${u.email}` : ''}</span>
                          </div>
                          <button style={{...st.btn('primary'),fontSize:11,padding:'4px 10px'}} onClick={()=>updateMemberTruck(u,truckId)}>Assign to this Truck</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {teamMsg && <div style={{ marginTop:10, fontSize:13, color: teamMsg.ok ? D.success : D.danger }}>{teamMsg.text}</div>}
              <div style={{marginTop:12,fontSize:12,color:D.muted}}>Need to reset a PIN or remove someone entirely? Head to Settings → Team.</div>
            </div>
          )}
        </div>
      )}

      {useModal && (
        <Overlay><div style={{background:D.card,border:`1px solid ${D.border}`,borderRadius:12,padding:24,width:'min(420px,95vw)'}}>
          <MHdr title={`Use "${useModal.desc}" on a Job`} onClose={()=>setUseModal(null)}/>
          <Field label={`Quantity (have ${useModal.qty_on_hand} ${useModal.unit})`}>
            <input style={st.input} type="number" min="0" max={useModal.qty_on_hand} value={useQty} onChange={e=>setUseQty(e.target.value)}/>
          </Field>
          <Field label="Job">
            <select style={st.input} value={useJobId} onChange={e=>setUseJobId(e.target.value)}>
              <option value="">— Select job —</option>
              {activeJobs.map(j=><option key={j.id} value={j.id}>{j.title}</option>)}
            </select>
          </Field>
          <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:16}}>
            <button style={st.btn()} onClick={()=>setUseModal(null)}>Cancel</button>
            <button style={st.btn('primary')} disabled={useBusy||!useQty||!useJobId} onClick={confirmUse}>{useBusy?'Using...':'Confirm'}</button>
          </div>
        </div></Overlay>
      )}
    </div>
  );
}
